// One time clean up on Purchase Returns / Sales Returns: lines a deleted
// return left behind in stone histories (before deletes removed them too).
// Checked once when the page's returns have loaded; shows a note with a
// Remove button only when something is found.

import { useEffect, useRef, useState } from 'react';
import { writeBatch, type DocumentReference } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { findOrphanReturnHistory, type ReturnKind } from '../utils/returnHistory';
import type { PurchaseReturn, SalesReturn } from '../types';

function IconNoteInfo() {
  return <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="6.3" stroke="currentColor" strokeWidth="1.4" />
    <path d="M8 7.2v3.6M8 5.1v.1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>;
}

export default function ReturnHistoryCleanup({ kind, returns, loading }: {
  kind: ReturnKind;
  returns: (PurchaseReturn | SalesReturn)[];
  loading: boolean;
}) {
  const [orphans, setOrphans] = useState<DocumentReference[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const checked = useRef(false);

  useEffect(() => {
    const db = getClientDb();
    if (loading || checked.current || !db) return;
    checked.current = true;
    findOrphanReturnHistory(db, kind, returns as never).then(setOrphans).catch(() => {});
  }, [loading, kind, returns]);

  async function remove() {
    const db = getClientDb();
    if (!db) return;
    setBusy(true);
    try {
      for (let i = 0; i < orphans.length; i += 400) {
        const b = writeBatch(db);
        orphans.slice(i, i + 400).forEach(r => b.delete(r));
        await b.commit();
      }
      setMsg(`Removed ${orphans.length} old line${orphans.length === 1 ? '' : 's'} from stone history.`);
      setOrphans([]);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Could not remove them.');
    } finally { setBusy(false); }
  }

  if (!orphans.length && !msg) return null;
  const label = kind === 'purchase' ? 'Returned to supplier' : 'Returned by customer';
  return (
    <div className="msg msg-note" style={{ marginBottom: 8 }}>
      <span className="msg-note-icon"><IconNoteInfo /></span>
      <span style={{ flex: 1 }}>
        {orphans.length
          ? `${orphans.length} "${label}" line${orphans.length === 1 ? ' is' : 's are'} still in stone history from returns that were deleted.`
          : msg}
      </span>
      {orphans.length > 0 && (
        <button type="button" className="line-item-link-btn" disabled={busy} onClick={remove}>
          {busy ? 'Removing…' : 'Remove them'}
        </button>
      )}
      <button type="button" className="line-item-link-btn" onClick={() => { setOrphans([]); setMsg(''); }}>Dismiss</button>
    </div>
  );
}
